// src/modules/StockMovements/adapters/ProductsApiAdapter.ts

import { HttpClient } from '@adapters/HttpClient';
import { Product } from '../models/Products';

export interface ProductFilters {
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  // Si en el futuro necesitas más filtros (e.g. category_id, status, searchTerm), agrégalos aquí:
  // category_id?: number;
  // status?: string;
  // searchTerm?: string;
}

export interface ProductPage {
  products: Product[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

export class ProductsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  // Valores por defecto para paginación / ordenamiento
  private static readonly DEFAULT_FILTERS = {
    sortBy: 'id',
    sortDirection: 'desc' as 'asc' | 'desc',
    page: 1,
    limit: 2000,
  };

  /**
   * Obtiene productos paginados desde el endpoint /products.
   * Acepta filtros básicos: sortBy, sortDirection, page, limit.
   */
  async getProductsPaginated(filters: ProductFilters = {}): Promise<ProductPage> {
    const {
      sortBy = ProductsApiAdapter.DEFAULT_FILTERS.sortBy,
      sortDirection = ProductsApiAdapter.DEFAULT_FILTERS.sortDirection,
      page = ProductsApiAdapter.DEFAULT_FILTERS.page,
      limit = ProductsApiAdapter.DEFAULT_FILTERS.limit,
      // category_id, status, searchTerm, etc. si se agregan
    } = filters;

    // Construir params para la petición
    const params: Record<string, any> = {
      fields: 'basic',
      order_by: sortBy,
      order_direction: sortDirection,
      page,
      per_page: limit,
    };

    // Llamada al endpoint
    const response = await this.httpClient.get<{
      data: any[];
      meta: {
        total: number;
        perPage: number;
        currentPage: number;
        lastPage: number;
      };
      links?: {
        first: string;
        prev: string | null;
        next: string | null;
        last: string;
      };
    }>('/products', params);

    // Extraer array crudo de items. Asumimos que cada item coincide con la interfaz Product (snake_case)
    const rawArray: any[] = response.data;

    const products: Product[] = rawArray as Product[];

    // Leer paginación desde response.meta
    const meta = response.meta;
    // Ajustar nombres si tu API usa current_page / last_page en lugar de currentPage / lastPage:
    const currentPage = meta.currentPage ?? (meta as any).current_page;
    const lastPage = meta.lastPage ?? (meta as any).last_page;
    const total = meta.total;

    return {
      products,
      nextPage:
        typeof currentPage === 'number' && typeof lastPage === 'number' && currentPage < lastPage
          ? currentPage + 1
          : undefined,
      hasMore: typeof currentPage === 'number' && typeof lastPage === 'number' ? currentPage < lastPage : false,
      total,
    };
  }

  async getAllProducts(): Promise<Product[]> {
    // Opción: solicitar primera página con limit grande (si tu API lo permite):
    const firstPage = await this.getProductsPaginated({
      page: 1,
      limit: Number.MAX_SAFE_INTEGER, // o el máximo que tu API tolere
    });
    return firstPage.products;
  }
}
